/*
 * NextGenn engine · before and after (sections/ngs-compare.liquid).
 *
 * One element per pair. The position is a single custom property, `--pos`, that
 * the stylesheet clips the top picture and places the handle with; this file
 * only decides what writes it.
 *
 * TWO WRITERS, and why both. The range input is the keyboard's and the screen
 * reader's: arrow keys nudge it and it is announced with a value, for free. But
 * iOS Safari only drags a range by its THUMB and ignores a touch anywhere else
 * on the track, so on an iPhone a full-size invisible range is a handle nobody
 * can hold. The finger therefore gets the picture itself, through pointer
 * events, and the input is kept out of the pointer's way in the stylesheet.
 *
 * WHICH TOUCHES ARE OURS is the stylesheet's call, not this file's: where an
 * element says `touch-action: pan-y` the browser hands us the sideways drag,
 * and where it does not (the picture, while the pairs are a swipeable rail) the
 * browser keeps it for scrolling. `owns()` reads that same property, so the
 * split never jerks under a thumb that was only on its way to the next pair.
 */
if (!customElements.get('ngs-compare')) {
  /* A finger has to travel this far sideways before it is a drag, so a touch
     that begins a scroll does not move the split on its way past. */
  const SLOP = 4;

  customElements.define(
    'ngs-compare',
    class NgsCompare extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('input[type="range"]');
        if (!this.range) return;
        this.press = null;

        this.onInput = () => this.set(Number(this.range.value), false);
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onEnd = this.onEnd.bind(this);

        this.range.addEventListener('input', this.onInput);
        this.addEventListener('pointerdown', this.onDown);
        this.addEventListener('pointermove', this.onMove);
        this.addEventListener('pointerup', this.onEnd);
        this.addEventListener('pointercancel', this.onEnd);

        this.set(Number(this.range.value), false);
      }

      disconnectedCallback() {
        if (!this.range) return;
        this.range.removeEventListener('input', this.onInput);
        this.removeEventListener('pointerdown', this.onDown);
        this.removeEventListener('pointermove', this.onMove);
        this.removeEventListener('pointerup', this.onEnd);
        this.removeEventListener('pointercancel', this.onEnd);
      }

      /* `fromPointer` also moves the input, so the keyboard carries on from
         wherever the finger left the handle. */
      set(value, fromPointer) {
        const position = Math.round(Math.min(100, Math.max(0, value)) * 100) / 100;
        this.style.setProperty('--pos', `${position}%`);
        if (fromPointer) this.range.value = String(Math.round(position));
        const shown = Math.round(position);
        this.range.setAttribute(
          'aria-valuetext',
          `${this.dataset.before || ''} ${shown}%, ${this.dataset.after || ''} ${100 - shown}%`.trim()
        );
      }

      /* The split under a pointer, in percent from the BEFORE side, which is
         the right-hand side on a right-to-left page. */
      at(clientX) {
        const rect = this.getBoundingClientRect();
        if (!rect.width) return Number(this.range.value);
        const rtl = getComputedStyle(this).direction === 'rtl';
        return ((rtl ? rect.right - clientX : clientX - rect.left) / rect.width) * 100;
      }

      /* Is a sideways drag that starts on this element ours? A mouse always
         is. A finger is only where the stylesheet has taken sideways panning
         away from the browser, on the element touched or any parent up to us. */
      owns(event) {
        if (event.pointerType === 'mouse') return true;
        for (let node = event.target; node && node !== this.parentNode; node = node.parentNode) {
          if (node.nodeType === 1 && getComputedStyle(node).touchAction === 'pan-y') return true;
        }
        return false;
      }

      capture(event) {
        try {
          this.setPointerCapture(event.pointerId);
        } catch (error) {
          /* A pointer the browser already released cannot be captured; the drag
             still works while it stays over the picture. */
        }
      }

      onDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.press = { id: event.pointerId, x: event.clientX, moved: false, ours: this.owns(event) };
        /* A mouse means it: the split goes where it pressed, and follows. */
        if (event.pointerType === 'mouse') {
          event.preventDefault();
          this.capture(event);
          this.press.moved = true;
          this.toggleAttribute('data-dragging', true);
          this.set(this.at(event.clientX), true);
        }
      }

      onMove(event) {
        const press = this.press;
        if (!press || press.id !== event.pointerId || !press.ours) return;
        if (!press.moved) {
          if (Math.abs(event.clientX - press.x) < SLOP) return;
          press.moved = true;
          this.capture(event);
          this.toggleAttribute('data-dragging', true);
        }
        this.set(this.at(event.clientX), true);
      }

      onEnd(event) {
        const press = this.press;
        if (!press || press.id !== event.pointerId) return;
        /* A tap that never moved puts the split where it landed, wherever on
           the picture that was: a tap is never the start of a swipe. */
        if (!press.moved && event.type === 'pointerup') this.set(this.at(event.clientX), true);
        this.press = null;
        this.toggleAttribute('data-dragging', false);
      }
    }
  );
}
